/**
 * What each Overview section on the page shows, for Kory: one short, bounded summary per section
 * (`data-overview-section`), so when Kory points at a section it can say what is in it. A section may
 * write its own (`data-kory-summary`: the schedule lists its items with times); otherwise it is the
 * section's rendered text, fields left out. A new section only needs its `data-overview-section` key.
 *
 * It rides in the `<page-context>` block (`capturePage`), and only when the page is shared.
 */
import { visibleText } from "@/lib/kory-page-context";
import { hiddenFromView } from "@/lib/kory-screen-digest";

/** One section's ceiling: a scorecard card's shown rows and rocks fit (at most a dozen capped lines),
 *  never the whole page. The backend's ``references.MAX_SECTION_SUMMARY`` sits just above it. */
export const SECTION_SUMMARY_MAX_CHARS = 1_800;
const MAX_LINE = 160;

const cap = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/** What `section` shows, as short lines: its own summary, or its rendered text (a table row's cells
 *  joined with " · "; each line capped). Capped at `SECTION_SUMMARY_MAX_CHARS`. A section's own summary
 *  keeps its lines whole: it is already bounded, and a cut line could drop a name or a number. */
export function sectionSummary(section: HTMLElement): string {
  const own = section.dataset.korySummary;
  const lines = (own ?? visibleText(section))
    .split("\n")
    .map((line) => line.replace(/\t+/g, " · ").replace(/\s+/g, " ").trim())
    .map((line) => (own === undefined ? cap(line, MAX_LINE) : line))
    .filter(Boolean);
  // Rendered text repeats a line where a label sits beside its value; once is enough. A section's own
  // lines are its items, each kept (two meetings can read the same).
  const kept = own === undefined ? lines.filter((line, i) => line !== lines[i - 1]) : lines;
  return cap(kept.join("\n"), SECTION_SUMMARY_MAX_CHARS);
}

/** Every shown Overview section's summary as an `<overview-sections>` block, in page order; null when
 *  the page has none. Nothing in a summary can close its tag early. */
export function sectionSummaries(root: ParentNode = document): string | null {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const section of root.querySelectorAll<HTMLElement>("[data-overview-section]")) {
    const key = section.dataset.overviewSection ?? "";
    if (!key || seen.has(key) || hiddenFromView(section)) continue;
    seen.add(key);
    const summary = sectionSummary(section).replace(/<\/?(overview-)?section/gi, "");
    if (summary) parts.push(`<section key="${key}">\n${summary}\n</section>`);
  }
  return parts.length ? `<overview-sections>\n${parts.join("\n")}\n</overview-sections>` : null;
}
